import React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check, Minus } from "lucide-react";
import { cn } from "@/utils/utils";

export interface CheckboxProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    "onCheckedChange"
  > {
  onCheckedChange?: (checked: boolean) => void;
  variant?: "default" | "partial";
  isCircle?: boolean;
}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    {
      className,
      onCheckedChange,
      checked,
      disabled,
      variant = "default",
      isCircle,
      ...props
    },
    ref
  ) => {
    const showDash = variant === "partial";

    return (
      <CheckboxPrimitive.Root
        ref={ref}
        className={cn(
          "peer h-4 w-4 shrink-0 border",
          "rounded-sm",
          "border-tertiary",
          "ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          checked && "bg-cta border-cta text-on-primary",
          disabled &&
            "cursor-not-allowed bg-buttons-cta-primary-disabled border-buttons-cta-primary-disabled",
          isCircle && "rounded-full",
          className
        )}
        checked={checked}
        onCheckedChange={(newChecked) =>
          !disabled && onCheckedChange?.(newChecked === true)
        }
        disabled={disabled}
        {...props}
      >
        <CheckboxPrimitive.Indicator
          className={cn(
            "flex items-center justify-center text-current",
            checked && "text-on-primary",
            disabled && "text-disabled"
          )}
        >
          {showDash ? (
            <Minus className="h-[14px] w-[14px]" />
          ) : (
            <Check className="h-[14px] w-[14px]" strokeWidth="2" />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    );
  }
);
Checkbox.displayName = "Checkbox";

export default Checkbox;
